// src/components/Admin/ManageStaff.js
import React, { useState, useEffect, useCallback } from 'react';
import { db, storage } from '../../firebase';
import Overlay from '../Overlay';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../../utils/cropImage';
import Slider from '@mui/material/Slider'; // 修正: @mui/material に変更
import Button from '@mui/material/Button'; // 修正: @mui/material に変更


function ManageStaff() {
  const [staffList, setStaffList] = useState([]);
  const [formState, setFormState] = useState({
    name: '',
    position: '',
    bio: '',
    photoFile: null,
    photoURL: '',
    id: null, // 編集時に使用
  });
  const [preview, setPreview] = useState(null); // プレビュー用
  const [showOverlay, setShowOverlay] = useState(false);

  // トリミング用の状態
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [isCropping, setIsCropping] = useState(false);

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      const snapshot = await db.collection("staff").orderBy("name").get();
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStaffList(data);
    } catch (error) {
      console.error("Error fetching staff:", error);
      alert('スタッフの取得に失敗しました。');
    }
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    setFormState(prev => ({ ...prev, photoFile: file }));
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
        setIsCropping(true); // トリミングモードに切り替え
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
      setIsCropping(false);
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const showCroppedImage = useCallback(async () => {
    try {
      const { blob, fileUrl } = await getCroppedImg(preview, croppedAreaPixels, zoom, 1);
      setCroppedImage(blob);
      setPreview(fileUrl); // プレビューをトリミング後の画像に更新
      setIsCropping(false);
    } catch (e) {
      console.error(e);
      alert('画像のトリミングに失敗しました。');
    }
  }, [preview, croppedAreaPixels, zoom]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (formState.id) {
      await updateStaff();
    } else {
      await addStaff();
    }
  };

  const addStaff = async () => {
    try {
      if (!croppedImage) {
        alert('写真をアップロードしてトリミングしてください。');
        return;
      }

      const storageRef = storage.ref(`staff/${Date.now()}_${formState.photoFile.name}`);
      await storageRef.put(croppedImage);
      const photoURL = await storageRef.getDownloadURL();

      await db.collection("staff").add({
        name: formState.name,
        position: formState.position,
        bio: formState.bio,
        photoURL,
      });

      alert('スタッフが追加されました。');
      resetForm();
      fetchStaff();
    } catch (error) {
      console.error("Error adding staff:", error);
      alert('スタッフの追加に失敗しました。');
    }
  };

  const updateStaff = async () => {
    try {
      let photoURL = formState.photoURL;
      if (croppedImage) {
        const storageRef = storage.ref(`staff/${Date.now()}_${formState.photoFile.name}`);
        await storageRef.put(croppedImage);
        photoURL = await storageRef.getDownloadURL();
      }

      await db.collection("staff").doc(formState.id).update({
        name: formState.name,
        position: formState.position,
        bio: formState.bio,
        photoURL,
      });

      alert('スタッフが更新されました。');
      resetForm();
      setShowOverlay(false);
      fetchStaff();
    } catch (error) {
      console.error("Error updating staff:", error);
      alert('スタッフの更新に失敗しました。');
    }
  };

  const deleteStaff = async (id, photoURL) => {
    if (!window.confirm('このスタッフを削除してもよろしいですか？')) return;
    try {
      await db.collection("staff").doc(id).delete();
      if (photoURL) {
        const storageRef = storage.refFromURL(photoURL);
        await storageRef.delete();
      }
      alert('スタッフが削除されました。');
      fetchStaff();
    } catch (error) {
      console.error("Error deleting staff:", error);
      alert('スタッフの削除に失敗しました。');
    }
  };

  const editStaff = staff => {
    setFormState({
      name: staff.name,
      position: staff.position,
      bio: staff.bio,
      photoFile: null,
      photoURL: staff.photoURL || '',
      id: staff.id,
    });
    setPreview(staff.photoURL || null);
    setCroppedImage(null);
    setIsCropping(false);
  };

  const viewStaff = staff => {
    setFormState({
      name: staff.name,
      position: staff.position,
      bio: staff.bio,
      photoFile: null,
      photoURL: staff.photoURL || '',
      id: staff.id,
    });
    setShowOverlay(true);
  };

  const resetForm = () => {
    setFormState({
      name: '',
      position: '',
      bio: '',
      photoFile: null,
      photoURL: '',
      id: null,
    });
    setPreview(null);
    setCroppedImage(null);
    setIsCropping(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  return (
    <div className="manage-staff">
      <h2>スタッフ紹介の管理</h2>
      <form onSubmit={handleSubmit}>
        <label>
          名前:
          <input 
            type="text" 
            name="name" 
            value={formState.name} 
            onChange={handleInputChange} 
            required 
          />
        </label>
        <label>
          職位:
          <input 
            type="text" 
            name="position" 
            value={formState.position} 
            onChange={handleInputChange} 
            required 
          />
        </label>
        <label>
          紹介文:
          <textarea 
            name="bio" 
            value={formState.bio} 
            onChange={handleInputChange} 
            required 
          />
        </label>
        <label>
          写真:
          <input 
            type="file" 
            accept="image/*" 
            onChange={handleFileChange} 
            required={!formState.id} // 編集時は必須ではない
          />
        </label>
        {isCropping && preview && (
          <div className="crop-container" style={{ position: 'relative', width: '300px', height: '300px', margin: '20px auto' }}>
            <Cropper
              image={preview}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
            <div className="controls" style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={(e, zoom) => setZoom(zoom)}
                style={{ width: '200px' }}
              />
              <Button variant="contained" color="primary" onClick={showCroppedImage} style={{ marginLeft: '10px' }}>
                トリミング
              </Button>
            </div>
          </div>
        )}
        {preview && !isCropping && (
          <div className="preview" style={{ textAlign: 'center', margin: '20px 0' }}>
            <p>プレビュー:</p>
            <img 
              src={preview} 
              alt="Preview" 
              style={{ 
                width: '150px', 
                height: '150px', 
                borderRadius: '50%', 
                objectFit: 'cover', 
                border: '2px solid #ccc' 
              }} 
            />
          </div>
        )}
        <Button type="submit" variant="contained" color="primary" className="button">
          {formState.id ? 'スタッフを更新' : 'スタッフを追加'}
        </Button>
        {formState.id && (
          <Button 
            type="button" 
            variant="outlined" 
            color="secondary" 
            onClick={resetForm}
            className="button"
            style={{ marginLeft: '10px' }}
          >
            キャンセル
          </Button>
        )}
      </form>

      <h3>既存のスタッフ</h3>
      <ul>
        {staffList.map(staff => (
          <li key={staff.id} className="admin-list-item" style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            {staff.photoURL && (
              <img 
                src={staff.photoURL} 
                alt={staff.name} 
                style={{ 
                  width: '100px', 
                  height: '100px', 
                  borderRadius: '50%', 
                  objectFit: 'cover', 
                  marginRight: '20px' 
                }} 
              />
            )}
            <div>
              <h4>{staff.name}</h4>
              <p>職位: {staff.position}</p>
              <p>{staff.bio}</p>
              <Button onClick={() => viewStaff(staff)} variant="outlined" color="primary" style={{ marginRight: '10px' }}>
                プレビュー
              </Button>
              <Button onClick={() => editStaff(staff)} variant="outlined" color="secondary" style={{ marginRight: '10px' }}>
                編集
              </Button>
              <Button onClick={() => deleteStaff(staff.id, staff.photoURL)} variant="outlined" color="error">
                削除
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {showOverlay && (
        <Overlay onClose={() => setShowOverlay(false)}>
          <h3>{formState.name}</h3>
          <p>職位: {formState.position}</p>
          {formState.photoURL && (
            <img 
              src={formState.photoURL} 
              alt={formState.name} 
              style={{ 
                width: '150px', 
                height: '150px', 
                borderRadius: '50%', 
                objectFit: 'cover', 
                marginBottom: '1rem' 
              }} 
            />
          )}
          <p>{formState.bio}</p>
        </Overlay>
      )}
    </div>
  );
}

export default ManageStaff;
